/**
 * Tiny localStorage helpers with React hooks + cross-tab sync.
 *
 * We roll our own rather than pulling in a library: the data is trivial, we
 * need custom events (the "storage" DOM event doesn't fire in the tab that
 * made the write), and we want per-key hooks that stay in sync across every
 * component mounted in the tab.
 */

import { useCallback, useEffect, useState } from 'react';

const CHANGE_EVENT = 'intake:storage-changed';

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson<T>(key: string, value: T | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { key } }));
  } catch {
    /* quota / privacy mode — drop silently */
  }
}

export function usePersistentState<T>(
  key: string,
  initial: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(() => readJson<T>(key) ?? initial);

  useEffect(() => {
    const onChange = (e: Event) => {
      const ev = e as CustomEvent<{ key: string }>;
      if (ev.detail?.key !== key) return;
      setState(readJson<T>(key) ?? initial);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key) return;
      setState(readJson<T>(key) ?? initial);
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [key, initial]);

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      setState((prev) => {
        const resolved =
          typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        writeJson(key, resolved);
        return resolved;
      });
    },
    [key],
  );

  return [state, set];
}

export const storage = { readJson, writeJson };
